import { Dialog } from "@headlessui/react";
import { ModalFoterBox, ModalHeaderBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { forwardRef, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { SelectFields } from "../../components/select/SelectFields";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ConstMessage } from "../../config/const";
import { TextField } from "../../components/fields/Fields";
import { validNumber1orMore, validNumber, } from "../../utility/ValidationUtilty";

export type BonusPointSettingsModalProp = {
  bonusPointSettings?: BonusPointSettings;
  onSubmit: (data: BonusPointSettings) => void;
  onSelectCancel?: () => void;
}

export interface BonusPointSettings {
  no: number;
  condition: string;
  _strNumOfDays: string;
  dateFrom: string;
  dateTo: string;
  _strNumOfTimes: string;
  _strBonusPoints: string;
}

const schema = yup.object().shape({
  // 条件
  condition: yup.string().required(ConstMessage.SELECTION_REQUIRED),
  // 日数
  _strNumOfDays: yup.string()
    .when('condition', {
      is: (condition: string) =>
      (condition == 'seqDay' || condition == 'seqWeek' || condition == 'seqMonth'
        || condition == 'periodDay' || condition == 'periodWeek' || condition == 'periodMonth'),
      then: yup.string().required()
    })
    .test((value, testContext) => {
      const condition: string = testContext.parent.condition;
      if (condition == 'seqDay' || condition == 'seqWeek' || condition == 'seqMonth') {
        const result = validNumber(value, testContext, true);
        if (result != true) {
          return result;
        } else if (result == true && Number(value) <= 1) {
          return testContext.createError({
            message: ConstMessage.ERR_VALID_NUMBER.replace('%1', '2')
          });
        }
      } else if (condition == 'periodDay' || condition == 'periodWeek' || condition == 'periodMonth') {
        return validNumber1orMore(value, testContext, true);
      }
      return true;
    }),
  // 開始年月日
  dateFrom: yup.string().test((value, testContext) => {
    const condition: string = testContext.parent.condition;
    if (condition != 'periodRange') return true;

    const dateFrom: Date = testContext.parent.dateFrom;
    if (!dateFrom) {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_INPUT
      });
    }
    return true;
  }),
  // 終了年月日
  dateTo: yup.string().test((value, testContext) => {
    const condition: string = testContext.parent.condition;
    if (condition != 'periodRange') return true;

    const dateFrom: Date = testContext.parent.dateFrom;
    const dateTo: Date = testContext.parent.dateTo;
    if (!dateTo) {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_INPUT
      });
    }
    if (!dateFrom) return true;
    if (dateFrom > dateTo) {
      return testContext.createError({
        message: "開始年月日より後を入力してください。"
      });
    }
    return true;
  }),
  // 回数
  _strNumOfTimes: yup.string()
    .test((value, testContext) => {
      const condition: string = testContext.parent.condition;
      if (!(condition == 'seqCount'
        || condition == 'periodDay' || condition == 'periodWeek' || condition == 'periodMonth'
        || condition == 'periodRange' || condition == 'sumCount'))
        return true;

      return validNumber1orMore(value, testContext, true);
    }),
  // ポイント数
  _strBonusPoints: yup.string()
    .test((value, testContext) => {
      const condition: string = testContext.parent.condition;
      if (!condition) return true;

      return validNumber1orMore(value, testContext, true);
    }),
});

const BonusPointSettingsModal = forwardRef(({
  open,
  toggle,
  closeOnClickOutside,
  bonusPointSettings,
  onSubmit,
}: ModalProp & BonusPointSettingsModalProp, ref) => {
  const [no, setNo] = useState(0);

  const conditions = [
    { id: "", name: "" },
    { id: "seqDay", name: "連続（日）" },
    { id: "seqWeek", name: "連続（週）" },
    { id: "seqMonth", name: "連続（月）" },
    { id: "periodDay", name: "期間（日）" },
    { id: "periodWeek", name: "期間（週）" },
    { id: "periodMonth", name: "期間（月）" },
    { id: "periodRange", name: "期間（範囲）" },
    { id: "sumCount", name: "合計（回数）" },
  ];

  const { control, handleSubmit, reset, setValue } = useForm<BonusPointSettings>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...bonusPointSettings,
    },
  });

  const [selectedCondition, setSelectedCondition] = useState<string>("");

  const handleCancel = () => {
    toggle();
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCondition(e.target.value);

    // 入力データとバリデーション表示をリセット
    reset({
      condition: e.target.value,
      _strNumOfDays: "",
      dateFrom: "",
      dateTo: "",
      _strNumOfTimes: "",
      _strBonusPoints: ""
    });
  };

  const handleSettingsSubmit = (data: BonusPointSettings) => {
    data.no = no;
    data.condition = selectedCondition;
    onSubmit(data);
    toggle();
  };

  useEffect(() => {
    if (bonusPointSettings) {
      setNo(bonusPointSettings.no);
      setValue("condition", bonusPointSettings.condition)
      setSelectedCondition(bonusPointSettings.condition);
      setValue("_strNumOfDays", bonusPointSettings._strNumOfDays);
      setValue("dateFrom", bonusPointSettings.dateFrom);
      setValue("dateTo", bonusPointSettings.dateTo);
      setValue("_strNumOfTimes", bonusPointSettings._strNumOfTimes);
      setValue("_strBonusPoints", bonusPointSettings._strBonusPoints);
    }
  }, [bonusPointSettings]);

  useEffect(() => {
    if (!open) {
      setSelectedCondition("");
      setValue("condition", "");
      setValue("_strNumOfDays", "");
      setValue("dateFrom", "");
      setValue("dateTo", "");
      setValue("_strNumOfTimes", "");
      setValue("_strBonusPoints", "");
      reset();
    };
  }, [open, reset]);

  return (
    <Modal open={open} toggle={toggle} closeOnClickOutside={closeOnClickOutside}>
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full max-w-[500px] min-w-[500px]">
        <form onSubmit={handleSubmit(handleSettingsSubmit)}>
          <div className="overflow-y-auto px-2 pb-4 sm:p-4 h-auto min-h-[380px]">
            <div className="text-center mt-3 mb-5">
              <ModalHeaderBox title={"ボーナスポイント設定"} />
            </div>
            <div>
              <div className="flex flex-wrap h-full pt-1 pb-1">
                <Controller
                  name={"condition"}
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-y-1">
                      <div className="flex items-center">
                        <div className="w-[7rem]">
                          <h3 className="text-sm text-gray-900 text-end">条件<span className="text-red-500 mx-1">*</span></h3>
                        </div>
                        <SelectFields
                          {...field}
                          label=""
                          color="indigo"
                          className="flex items-center gap-x-3 h-10"
                          selectClassName="py-2 h-auto"
                          onChange={handleConditionChange}
                          selectItems={conditions}
                          value={selectedCondition}
                          error={fieldState.error}
                          style={{ 'width': '9rem' }}
                        />
                      </div>
                      {fieldState.error?.message && (
                        <div className="block text-xs text-red-500" style={{ marginLeft: '126px' }}>{fieldState.error.message}</div>
                      )}
                    </div>
                  )}
                />
              </div>
              {selectedCondition === "" ? (<></>) : (
                <>
                  {selectedCondition.endsWith("Day") || selectedCondition.endsWith("Week") || selectedCondition.endsWith("Month") ? (
                    <>
                      <div className="h-full pt-1 pb-1">
                        <Controller
                          name={"_strNumOfDays"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-y-1">
                              <div className="flex items-center">
                                <div className="w-[7rem]">
                                  <h3 className="text-sm text-gray-900 text-end">
                                    {
                                      selectedCondition.endsWith("Day") ? "日数" :
                                        selectedCondition.endsWith("Week") ? "週数" :
                                          selectedCondition.endsWith("Month") ? "月数" : ""
                                    }
                                    <span className="text-red-500 mx-1">*</span>
                                  </h3>
                                </div>
                                <TextField
                                  {...field}
                                  type={"text"}
                                  label={""}
                                  color={"indigo"}
                                  className={"flex items-center gap-x-3 w-[7rem] h-10"}
                                  selectClassName={"px-3 py-2 h-auto"}
                                  style={{ 'width': '6rem', 'text-align': 'right' }}
                                  maxLength={3}
                                />
                                <label className="text-gray-700 text-sm">
                                  {
                                    selectedCondition.endsWith("seqDay") ? "日連続" :
                                      selectedCondition.endsWith("seqWeek") ? "週連続" :
                                        selectedCondition.endsWith("seqMonth") ? "ヵ月連続" :
                                          selectedCondition.endsWith("periodDay") ? "日間" :
                                            selectedCondition.endsWith("periodWeek") ? "週間" :
                                              selectedCondition.endsWith("periodMonth") ? "ヵ月間" : ""
                                  }
                                </label>
                              </div>
                              {fieldState.error?.message && (
                                <div className="block text-xs text-red-500" style={{ marginLeft: '126px' }}>{fieldState.error.message}</div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </>
                  ) : (
                    <></>
                  )}
                  {selectedCondition !== "periodRange" ? (
                    <></>
                  ) : (
                    <>
                      <div className="h-full pt-1 pb-1">
                        <Controller
                          name={"dateFrom"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-y-1">
                              <div className="flex items-center">
                                <div className="w-[7rem]">
                                  <h3 className="text-sm text-gray-900 text-end">
                                    {"開始年月日"}
                                    <span className="text-red-500 mx-1">*</span></h3>
                                </div>
                                <TextField
                                  {...field}
                                  type={"date"}
                                  label={""}
                                  color={"indigo"}
                                  className={"flex items-center gap-x-3 h-10"}
                                  selectClassName={"px-3 py-2 h-auto"}
                                  style={{ 'width': '9rem' }}
                                />
                              </div>
                              {fieldState.error?.message && (
                                <div className="block text-xs text-red-500" style={{ marginLeft: '126px' }}>{fieldState.error.message}</div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                      <div className="h-full pt-1 pb-1">
                        <Controller
                          name={"dateTo"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-y-1">
                              <div className="flex items-center">
                                <div className="w-[7rem]">
                                  <h3 className="text-sm text-gray-900 text-end">
                                    {"終了年月日"}
                                    <span className="text-red-500 mx-1">*</span></h3>
                                </div>
                                <TextField
                                  {...field}
                                  type={"date"}
                                  label={""}
                                  color={"indigo"}
                                  className={"flex items-center gap-x-3 h-10"}
                                  selectClassName={"px-3 py-2 h-auto"}
                                  style={{ 'width': '9rem' }}
                                />
                              </div>
                              {fieldState.error?.message && (
                                <div className="block text-xs text-red-500" style={{ marginLeft: '126px' }}>{fieldState.error.message}</div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </>)}
                  {(selectedCondition.startsWith("period") || selectedCondition.endsWith("Count")) ? (
                    <>
                      <div className="h-full pt-1 pb-1">
                        <Controller
                          name={"_strNumOfTimes"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex items-center">
                              <></>
                              <div className="flex flex-col gap-y-1">
                                <div className="flex items-center">
                                  <div className="w-[7rem]">
                                    <h3 className="text-sm text-gray-900 text-end">回数<span className="text-red-500 mx-1">*</span></h3>
                                  </div>
                                  <TextField
                                    {...field}
                                    type={"text"}
                                    label={""}
                                    color={"indigo"}
                                    className={"flex items-center gap-x-3 w-[7rem] h-10"}
                                    selectClassName={"px-3 py-2 h-auto"}
                                    style={{ 'width': '6rem', 'text-align': 'right' }}
                                    maxLength={3}
                                  />
                                  <label className="text-gray-700 text-sm">
                                    {
                                      selectedCondition.endsWith("seqCount") ? "回連続" :
                                        selectedCondition.endsWith("sumCount") ? "回" : ""
                                    }
                                  </label>
                                </div>
                                {fieldState.error?.message && (
                                  <div className="block text-xs text-red-500" style={{ marginLeft: '126px' }}>{fieldState.error.message}</div>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </>) : (
                    <></>
                  )}
                  <div className="h-full pt-1 pb-1">
                    <Controller
                      name={"_strBonusPoints"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <div className="flex flex-col gap-y-1">
                          <div className="flex items-center">
                            <div className="w-[7rem]">
                              <h3 className="text-sm text-gray-900 text-end">
                                {"ポイント数"}
                                <span className="text-red-500 mx-1">*</span>
                              </h3>
                            </div>
                            <TextField
                              {...field}
                              type={"text"}
                              label={""}
                              color={"indigo"}
                              className={"flex items-center gap-x-3 h-10"}
                              selectClassName={"px-3 py-2 h-auto"}
                              style={{ 'width': '7rem', 'text-align': 'right' }}
                              maxLength={5}
                            />
                          </div>
                          {fieldState.error?.message && (
                            <div className="block text-xs text-red-500" style={{ marginLeft: '126px' }}>{fieldState.error.message}</div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </>)}
            </div>
          </div>

          <ModalFoterBox>
            <Button
              className="mx-2 mt-1 w-[132px]"
              outline
              onClick={handleCancel}
              children={"キャンセル"}
            />
            <Button
              className="mt-1 mr-2 w-[132px]"
              color="indigo"
              onClick={handleSubmit(handleSettingsSubmit)}
              children={"登録"}
            />
          </ModalFoterBox>
        </form>
      </Dialog.Panel>
    </Modal>
  );
});

BonusPointSettingsModal.displayName = "BonusPointSettingsModal";
export default BonusPointSettingsModal;